import { useQuery } from "@tanstack/react-query";
import {
  KnowledgeStorage,
  KnowledgeStorageFilters,
  KnowledgeStorageResponse,
} from "../types/knowledge-storage";
import { ColumnState } from "@/shared/components/ui/category-filter";
import { get, makeApiError } from "@/shared/utils/fetchApi";

interface StorageListResponse {
  data: KnowledgeStorage[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Define the interface with knowledgeBaseId
interface KnowledgeStorageQueryFilters extends KnowledgeStorageFilters {
  knowledgeBaseId: string;
  selectedColumns?: ColumnState;
  filters?: Record<string, string[]>; // Adicionar suporte para filtros de categoria
  searchFieldType?: "text" | "date"; // Adicionar suporte para tipo de campo de busca
}

const fetchKnowledgeStorage = async (
  filters: KnowledgeStorageQueryFilters,
): Promise<KnowledgeStorageResponse> => {
  // Sem knowledgeBaseId não há o que buscar: devolve vazio em vez de chamar a
  // api com filtro incompleto. O `enabled` da query já cobre o caso normal.
  if (!filters.knowledgeBaseId) {
    return {
      data: [],
      total_pages: 0,
      total_items: 0,
      total: 0,
      page: 1,
      pageSize: 10,
    };
  }

  const params = new URLSearchParams();
  if (filters.page) params.append("page", filters.page.toString());
  if (filters.pageSize) params.append("pageSize", filters.pageSize.toString());
  if (filters.type) params.append("type", filters.type);
  if (filters.category) params.append("category", filters.category);
  if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);

  if (filters.searchFieldType === "date") {
    params.append("isDateSearch", "true");
    if (filters.searchTerm) {
      params.append("startDate", filters.searchTerm);
    }
  } else {
    params.append("isDateSearch", "false");
  }

  if (filters.filters) {
    for (const [key, values] of Object.entries(filters.filters)) {
      if (values && values.length > 0) {
        params.append(`filter_${key}`, values.join(","));
      }
    }
  }

  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  if (filters.selectedColumns) {
    const activeColumns = Object.entries(filters.selectedColumns)
      .filter(([_, isActive]) => isActive)
      .map(([columnId]) => columnId);

    if (activeColumns.length > 0) {
      params.append("columns", activeColumns.join(","));
    }
  }

  const endpoint = `/knowledge-bases/${filters.knowledgeBaseId}/storage`;
  const urlWithParams = params.toString()
    ? `${endpoint}?${params.toString()}`
    : endpoint;
  const res = await get<StorageListResponse>(urlWithParams);
  if (!res || !Array.isArray(res.data)) {
    throw makeApiError("Invalid response format", {
      code: "E_UNEXPECTED_RESPONSE",
    });
  }
  // v2 → legacy envelope (total_pages/total_items aliases pra compat consumers).
  return {
    data: res.data,
    total: res.total,
    total_items: res.total,
    total_pages: res.totalPages,
    page: res.page,
    pageSize: res.pageSize,
  };
};

export function useKnowledgeStorage(
  knowledgeBaseId: string,
  filters: KnowledgeStorageFilters = {},
  selectedColumns?: ColumnState,
) {
  return useQuery({
    queryKey: ["knowledge-storage", knowledgeBaseId, filters, selectedColumns],
    queryFn: () =>
      fetchKnowledgeStorage({ knowledgeBaseId, ...filters, selectedColumns }),
    enabled: !!knowledgeBaseId,
    placeholderData: (previousData) => previousData, // Use this instead of keepPreviousData
    staleTime: 0, // Alterado de 5 minutos para 0, forçando recarregar os dados
    refetchOnMount: true, // Garante que os dados sejam recarregados quando o componente for montado
    retry: false,
  });
}
